import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { CheckCircle2, Phone, Truck } from "lucide-react";
import api, { formatPrice, formatApiErrorDetail } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

export default function Checkout() {
  const { items, total, clearCart } = useCart();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [form, setForm] = useState({
    name: "", phone: "", email: "", county: "", city: "", address: "", notes: "",
  });

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.county || !form.city || !form.address) {
      toast.error("Completează toate câmpurile obligatorii");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/orders", {
        customer: form,
        items: items.map((i) => ({ product_id: i.product_id, name: i.name, price: i.price, quantity: i.quantity, image: i.image })),
        payment_method: "Plata la livrare (ramburs)",
      });
      clearCart();
      setDone(res.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="mx-auto flex max-w-xl flex-col items-center px-6 py-24 text-center" data-testid="checkout-success">
        <div className="grid h-20 w-20 place-items-center rounded-full bg-emerald-100 text-emerald-600">
          <CheckCircle2 className="h-10 w-10" />
        </div>
        <h1 className="mt-6 font-display text-3xl font-black text-slate-900">Comandă plasată cu succes!</h1>
        <p className="mt-3 text-slate-600">
          Mulțumim, <strong>{done.customer.name}</strong>! Comanda ta în valoare de{" "}
          <strong>{formatPrice(done.total)}</strong> a fost înregistrată. Te vom contacta telefonic la{" "}
          <strong>{done.customer.phone}</strong> pentru confirmare.
        </p>
        <div className="mt-8 flex gap-3">
          <Button asChild className="rounded-full bg-sky-600 px-6 hover:bg-sky-500"><Link to="/produse">Continuă cumpărăturile</Link></Button>
          <Button asChild variant="outline" className="rounded-full px-6"><Link to="/contul-meu">Comenzile mele</Link></Button>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="mx-auto max-w-xl px-6 py-24 text-center">
        <h1 className="font-display text-2xl font-black text-slate-900">Coșul tău este gol</h1>
        <Button asChild className="mt-6 rounded-full bg-sky-600 px-6 hover:bg-sky-500"><Link to="/produse">Vezi produsele</Link></Button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-6 py-12 lg:px-12" data-testid="checkout-page">
      <h1 className="mb-8 font-display text-3xl font-black tracking-tight text-slate-900 lg:text-4xl">Finalizare comandă</h1>
      <form onSubmit={submit} className="grid gap-10 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <div className="rounded-2xl border border-slate-100 bg-white p-6 sm:p-8">
            <h2 className="mb-5 font-display text-lg font-bold text-slate-900">Date de contact și livrare</h2>
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <Label htmlFor="name">Nume complet *</Label>
                <Input id="name" value={form.name} onChange={update("name")} className="mt-1.5 h-12" data-testid="checkout-name" required />
              </div>
              <div>
                <Label htmlFor="phone">Telefon *</Label>
                <Input id="phone" type="tel" inputMode="tel" value={form.phone} onChange={update("phone")} className="mt-1.5 h-12" data-testid="checkout-phone" required />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" inputMode="email" value={form.email} onChange={update("email")} className="mt-1.5 h-12" data-testid="checkout-email" />
              </div>
              <div>
                <Label htmlFor="county">Județ *</Label>
                <Input id="county" value={form.county} onChange={update("county")} className="mt-1.5 h-12" data-testid="checkout-county" required />
              </div>
              <div>
                <Label htmlFor="city">Localitate *</Label>
                <Input id="city" value={form.city} onChange={update("city")} className="mt-1.5 h-12" data-testid="checkout-city" required />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="address">Adresă (stradă, număr, bloc) *</Label>
                <Input id="address" value={form.address} onChange={update("address")} className="mt-1.5 h-12" data-testid="checkout-address" required />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="notes">Observații (opțional)</Label>
                <Textarea id="notes" value={form.notes} onChange={update("notes")} className="mt-1.5" data-testid="checkout-notes" rows={3} />
              </div>
            </div>
          </div>

          <div className="flex items-start gap-3 rounded-2xl border border-emerald-100 bg-emerald-50/70 p-5" data-testid="checkout-free-shipping">
            <Truck className="mt-0.5 h-5 w-5 text-emerald-600" />
            <div className="text-sm text-slate-600">
              <p className="font-bold text-emerald-700">Transport GRATUIT la toate comenzile</p>
              <p>Plata la livrare (ramburs) — plătești curierului la primire, fără plată online.</p>
            </div>
          </div>
        </div>

        <div className="h-fit rounded-2xl border border-slate-100 bg-white p-6 shadow-[0_8px_32px_rgba(2,132,199,0.05)]">
          <h2 className="font-display text-lg font-bold text-slate-900">Comanda ta</h2>
          <div className="mt-5 space-y-3">
            {items.map((i) => (
              <div key={i.product_id} className="flex justify-between gap-3 text-sm">
                <span className="text-slate-600">{i.name} <span className="text-slate-400">× {i.quantity}</span></span>
                <span className="shrink-0 font-semibold text-slate-900">{formatPrice(i.price * i.quantity)}</span>
              </div>
            ))}
          </div>
          <div className="mt-3 flex justify-between text-sm">
            <span className="text-slate-600">Transport</span>
            <span className="font-bold text-emerald-600" data-testid="checkout-shipping">GRATUIT</span>
          </div>
          <div className="my-5 h-px bg-slate-100" />
          <div className="flex items-center justify-between">
            <span className="font-display text-lg font-bold text-slate-900">Total</span>
            <span className="font-display text-2xl font-black text-slate-900" data-testid="checkout-total">{formatPrice(total)}</span>
          </div>
          <Button type="submit" size="lg" disabled={submitting} className="mt-6 w-full rounded-full bg-emerald-600 text-base hover:bg-emerald-500" data-testid="checkout-submit">
            {submitting ? "Se trimite..." : "Plasează comanda"}
          </Button>
          <p className="mt-4 flex items-center justify-center gap-1.5 text-xs text-slate-400">
            <Phone className="h-3 w-3" /> Te contactăm pentru confirmare
          </p>
        </div>
      </form>
    </div>
  );
}
